import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import sofixHeroImage from '../file/sofix/about-sofix-mieru-kv.jpg';
import sofixMaterialCirculationImage from '../file/sofix/SOFIX material-circulation.png';
import sofixNitrogenCycleImage from '../file/sofix/SOFIX mieru-2-1-s.png';
import sofixPhosphorusCycleImage from '../file/sofix/SOFIX mieru-3-1-s リン酸循環.png';
import Seo from '../components/Seo';

const keyPoints = [
  {
    title: '化学肥料だけに頼るとどうなるか',
    body:
      '化学肥料は即効性がありますが、土の中で有機物を分解し、養分を巡らせる微生物の働きまで十分に見ているわけではありません。SOFIXは、その土台が動いているかを見るための検査です。',
  },
  {
    title: '農薬の影響をどう考えるか',
    body:
      'SOFIXは残留農薬を直接測る検査ではありません。ただ、殺菌剤などの影響で土の生きものの働きが弱っていないか、総細菌数や循環の項目から読み取る手がかりになります。',
  },
  {
    title: 'なぜ数値の意味を知る必要があるか',
    body:
      '総細菌数、窒素循環、リン循環、TC・TN・C/Nは、それぞれ別の数字ではなく、化学肥料や農薬が土にどのような状態を残しているかを見るためにつながっています。',
  },
];

const biomassPoints = [
  {
    label: 'TC',
    body: '全炭素。土の中に、微生物のえさになり得る炭素がどれくらいあるかを見る指標です。',
  },
  {
    label: 'TN',
    body: '全窒素。土の中に蓄えられた窒素の量を見ます。',
  },
  {
    label: 'C/N',
    body: '炭素と窒素の比率。有機物の質のバランスを見るための目安です。',
  },
];

export default function SofixInfoPage() {
  useScrollReveal();

  return (
    <>
      <Seo
        title="SOFIXとは？"
        description="SOFIXの見方や、化学肥料・農薬と土の関係を一般の方にも分かりやすく整理した解説ページです。"
        path="/sofix"
        image={sofixHeroImage}
      />

      <section
        className="sofix-hero sofix-hero-with-image reveal"
        data-reveal
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(255, 255, 255, 0.72) 0%, rgba(241, 246, 243, 0.76) 100%), url("${sofixHeroImage}")`,
        }}
      >
        <div className="sofix-hero-copy">
          <p className="eyebrow">SOFIX Guide</p>
          <h1 className="sofix-title">
            SOFIXとは？
            <br />
            化学肥料や農薬と、
            <br />
            土の関係を見るための入口です。
          </h1>
          <p>
            Drs.FarmでSOFIXを紹介している理由は、土の中で本来起きている循環が、
            きちんと動いているかを知るためです。化学肥料や農薬は便利さをもたらす一方で、
            土の中の微生物や養分の巡りに影響を与えることがあります。SOFIXは、その結果として
            土がどんな状態になっているかを読み解くための検査です。
          </p>
          <div className="hero-actions">
            <Link to="/farmers" className="btn btn-primary">参加農家を見る</Link>
            <a href="#sofix-reading" className="btn btn-secondary">見方を知る</a>
          </div>
          <p className="meta-text sofix-hero-credit">
            背景図引用: SOFIX「about SOFIX」
            {' '}
            <a href="https://sofixbio.com/about-sofix/" target="_blank" rel="noreferrer">https://sofixbio.com/about-sofix/</a>
          </p>
        </div>
      </section>

      <section className="panel reveal sofix-points" data-reveal>
        <header className="section-head">
          <h2 className="sofix-heading">
            最初に見るべきなのは、
            <br />
            土の中の循環が動いているかです。
          </h2>
          <p className="section-subcopy">
            植物がつくった有機物は、落ち葉や糞尿、残さなどとして土に戻り、微生物によって分解され、
            再び植物が使える形へと変わっていきます。SOFIXは、この物質循環が今も機能しているかを見ています。
          </p>
        </header>
        <div className="sofix-checklist-grid">
          {keyPoints.map((point) => (
            <article key={point.title} className="sofix-check-card">
              <h3>{point.title}</h3>
              <p>{point.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        className="numbered-story reveal sofix-editorial sofix-editorial-single sofix-material-section"
        data-reveal
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(255, 255, 255, 0.84) 0%, rgba(241, 246, 243, 0.88) 100%), url("${sofixMaterialCirculationImage}")`,
        }}
      >
        <div className="numbered-story-copy">
          <p className="number-label">01 MATERIAL CIRCULATION</p>
          <h2 className="sofix-heading">
            土の中では、
            <br />
            物質が巡ることで
            <br />
            作物が育ちます。
          </h2>
          <p>
            自然環境の中では、植物が空気中の二酸化炭素と土壌中の無機物から有機物をつくり、
            その有機物が再び土に戻り、微生物によって分解され、また植物の栄養源へとつながっていきます。
            この循環があることで、土は作物を支える力を保ちます。
          </p>
          <p>
            一方で、化学肥料を中心にした農業が広がると、即効性や生産性は高まりましたが、
            微生物のえさになる有機物が入りにくくなり、土の中の生きものが少なくなる圃場も出てきました。
            SOFIXは、その循環が今どこまで保たれているかを確認するための技術として位置づけられています。
          </p>
          <p className="meta-text">
            背景図引用: SOFIX「about SOFIX」
            {' '}
            <a href="https://sofixbio.com/about-sofix/" target="_blank" rel="noreferrer">https://sofixbio.com/about-sofix/</a>
          </p>
        </div>
      </section>

      <section className="panel reveal sofix-glossary" data-reveal>
        <header className="section-head">
          <h2 className="sofix-heading">
            総微生物量（総細菌数）は、
            <br />
            土の生命力を見る入口です。
          </h2>
          <p className="section-subcopy">
            SOFIXでは、土壌1gの中に何億個の細菌がいるかを見ます。土壌中から細菌のDNAを抽出し、
            DNA量を指標として総細菌数を測る方法が eDNA 法です。培養しにくい細菌も含めて把握できるため、
            土の中の生きものの量をつかむ入口として使われています。
          </p>
        </header>
        <div className="sofix-checklist-grid">
          <article className="sofix-check-card">
            <h3>eDNA法で見ていること</h3>
            <p>
              土の中にいる細菌のDNA量と細菌数は比例するため、DNA量から土壌中の総細菌数を推定できます。
              SOFIXでは、この方法で土の中の微生物量を見える化しています。
            </p>
          </article>
          <article className="sofix-check-card">
            <h3>2億個/g-土壌 以上</h3>
            <p>
              これまでの研究では、総細菌数が2億個以上あると、土壌の物質循環が動き始める目安になるとされています。
            </p>
          </article>
          <article className="sofix-check-card">
            <h3>6億個/g-土壌 以上</h3>
            <p>
              総細菌数が6億個以上に増えてくると、窒素循環やリン循環などの働きがより活発になりやすいとされています。
            </p>
          </article>
          <article className="sofix-check-card">
            <h3>0.66億個/g-土壌 未満</h3>
            <p>
              検出限界以下まで下がると、土の循環系がかなり弱っている可能性があります。長期に化学肥料だけに頼る圃場や、
              殺菌剤などの影響が強い圃場では、総細菌数が低くなることがあります。
            </p>
          </article>
        </div>
      </section>

      <section className="numbered-story reveal sofix-editorial" data-reveal>
        <figure className="sofix-reference-figure">
          <img
            src={sofixNitrogenCycleImage}
            alt="SOFIXの窒素循環活性を説明する図"
            loading="lazy"
          />
          <figcaption className="meta-text">
            引用: SOFIX「about SOFIX」
            {' '}
            <a href="https://sofixbio.com/about-sofix/" target="_blank" rel="noreferrer">https://sofixbio.com/about-sofix/</a>
          </figcaption>
        </figure>
        <div className="numbered-story-copy">
          <p className="number-label">02 NITROGEN CYCLE</p>
          <h2 className="sofix-heading">
            窒素循環活性は、
            <br />
            肥料を作物が使える形へ
            <br />
            変える力です。
          </h2>
          <p>
            土の中では、<strong>窒素の循環</strong>がとても重要です。
            有機肥料や作物残さなどの有機物は、土の中にいる<strong>細菌（微生物）</strong>によって分解され、
            アンモニア→亜硝酸→硝酸へと形を変えながら、作物が吸える窒素になります。
          </p>
          <p>
            SOFIXは、この「<strong>分解して、作物が使える形へ変える力</strong>」を、
            <strong>アンモニア酸化活性</strong>と<strong>亜硝酸酸化活性</strong>（＋微生物量）として見ています。
            つまり窒素循環活性は、「肥料が入っているか」ではなく、
            「土の中で窒素がきちんと巡り、使える状態になっているか」を読むための指標です。
          </p>
        </div>
      </section>

      <section className="numbered-story reveal sofix-editorial" data-reveal>
        <div className="numbered-story-copy">
          <p className="number-label">03 PHOSPHORUS CYCLE</p>
          <h2 className="sofix-heading">
            リン循環活性は、
            <br />
            リンを使える形へ変える
            <br />
            土の力を見ています。
          </h2>
          <p>
            土の中では、<strong>リンの循環</strong>も同じくらい大事です。
            有機肥料などに含まれる有機態リンは、土の中の<strong>細菌（微生物）</strong>の働きで分解され、
            作物が吸える<strong>リン酸</strong>へと変わっていきます。
          </p>
          <p>
            SOFIXで見ている<strong>リン循環活性</strong>は、この「有機態リン → リン酸」への変換の勢い（活性度）を示します。
            リンを多く入れていても、循環が弱いと固定されやすく、作物が利用しにくいことがあります。
          </p>
        </div>
        <figure className="sofix-reference-figure">
          <img
            src={sofixPhosphorusCycleImage}
            alt="SOFIXのリン循環活性を説明する図"
            loading="lazy"
          />
          <figcaption className="meta-text">
            引用: SOFIX「about SOFIX」
            {' '}
            <a href="https://sofixbio.com/about-sofix/" target="_blank" rel="noreferrer">https://sofixbio.com/about-sofix/</a>
          </figcaption>
        </figure>
      </section>

      <section className="panel reveal sofix-glossary" data-reveal>
        <header className="section-head">
          <h2 className="sofix-heading">
            土壌バイオマス量は、
            <br />
            微生物が働きやすい環境かを見ています。
          </h2>
          <p className="section-subcopy">
            SOFIXでは、土の中の有機物量についても評価します。主に全炭素（TC）と全窒素（TN）、そしてC/N比を見て、
            微生物のえさになる有機物の量とバランスが整っているかを確認します。
          </p>
        </header>
        <div className="sofix-checklist-grid">
          {biomassPoints.map((point) => (
            <article key={point.label} className="sofix-check-card">
              <h3>{point.label}</h3>
              <p>{point.body}</p>
            </article>
          ))}
          <article className="sofix-check-card">
            <h3>畑での目安</h3>
            <p>
              畑では、TCが25,000mg/kg以上、TNが1,500mg/kg以上で、C/N比が10〜25の範囲にあると、
              微生物のえさになる有機物の量と比率が比較的よい状態とされ、窒素循環活性も高まりやすいとされています。
            </p>
          </article>
        </div>
      </section>

      <section className="panel reveal sofix-checklist" data-reveal id="sofix-reading">
        <header className="section-head">
          <h2 className="sofix-heading">
            原本PDFを見るときは、
            <br />
            この順番で見ると分かりやすくなります。
          </h2>
          <p className="section-subcopy">
            数字を一つずつ追うより、化学肥料や農薬との関係を意識しながら、土の生命力と循環を見る順番で読むのが分かりやすいです。
          </p>
        </header>
        <div className="sofix-reading-list">
          <article className="sofix-reading-step">
            <p className="number-label">STEP 1</p>
            <h3>総細菌数を見る</h3>
            <p>まず、土の中に生きものがどれくらいいるかを見ます。ここが土台です。</p>
          </article>
          <article className="sofix-reading-step">
            <p className="number-label">STEP 2</p>
            <h3>窒素循環とリン循環を見る</h3>
            <p>肥料を土が使える形へ変える力が動いているかを確認します。</p>
          </article>
          <article className="sofix-reading-step">
            <p className="number-label">STEP 3</p>
            <h3>TC・TN・C/N・pH・ECを見る</h3>
            <p>有機物の量やバランス、土の環境が極端に偏っていないかを見ます。</p>
          </article>
          <article className="sofix-reading-step">
            <p className="number-label">STEP 4</p>
            <h3>農家さんの栽培の哲学と合わせて読む</h3>
            <p>数字だけで判断せず、その農家さんがどんな土づくりをしているかと一緒に見ることが大切です。</p>
          </article>
        </div>
      </section>

      <section className="maker-section reveal sofix-bottom" data-reveal>
        <div className="maker-copy">
          <p className="eyebrow">For Visitors</p>
          <h2 className="sofix-heading">
            SOFIXは、
            <br />
            数字を並べるためではなく、
            <br />
            土の背景を知るためにあります。
          </h2>
          <p>
            Drs.Farmでは、SOFIXの数値を「専門家だけの検査結果」で終わらせず、
            化学肥料や農薬と土の関係を一般の方にも考えていただく入口として公開していきます。
            土の状態を知ることで、その農家さんが何を大切にしているかも見えてきます。
          </p>
          <div className="hero-actions">
            <Link to="/farmers" className="btn btn-primary">参加農家を見る</Link>
            <Link to="/" className="btn btn-secondary">トップへ戻る</Link>
          </div>
        </div>
        <div className="sofix-image-slot sofix-image-slot-secondary" aria-label="SOFIX補助画像スペース">
          <div className="sofix-image-slot-inner">
            <p className="sofix-slot-label">IMAGE AREA</p>
            <p>ここにSOFIXの注釈付き画像や比較図を配置できます</p>
          </div>
        </div>
      </section>
    </>
  );
}
